<template>
  <div>
    <input
      ref="draftFileInput"
      type="file"
      accept=".json,application/json"
      class="d-none"
      @change="handleDraftFileSelected"
    />

    <v-dialog
      :value="value"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @input="handleInput"
    >
      <v-card class="portfolio-year-end-transition d-flex flex-column">
        <v-card-title class="align-start portfolio-year-end-transition__header">
          <div>
            <h3 class="card-label mb-1">Yenileme / Geçiş Aracı</h3>
            <div class="text-muted">
              Seçilen bitiş tarihinde kapanan kayıtlar için fiyat artışı ve ürün
              geçişlerini tek akışta hazırlayın.
            </div>
          </div>

          <v-spacer />

          <v-btn outlined small color="primary" @click="closeDialog">
            Kapat
          </v-btn>
        </v-card-title>

        <v-card-text
          class="portfolio-year-end-transition__content flex-grow-1 d-flex flex-column"
        >
          <v-stepper
            v-model="currentStep"
            flat
            class="portfolio-year-end-transition__stepper d-flex flex-column flex-grow-1"
          >
            <v-stepper-header
              class="portfolio-year-end-transition__stepper-header"
            >
              <v-stepper-step
                :complete="currentStepNumber > 1"
                :editable="canNavigateCompletedSteps && currentStepNumber > 1"
                step="1"
                color="primary"
              >
                Operasyon
              </v-stepper-step>
              <v-divider />
              <v-stepper-step
                :complete="currentStepNumber > 2"
                :editable="canNavigateCompletedSteps && currentStepNumber > 2"
                step="2"
                color="primary"
              >
                Adaylar
              </v-stepper-step>
              <v-divider />
              <v-stepper-step
                :complete="currentStepNumber > 3"
                :editable="canNavigateCompletedSteps && currentStepNumber > 3"
                step="3"
                color="primary"
              >
                Önizleme
              </v-stepper-step>
              <v-divider />
              <v-stepper-step step="4" color="primary"> Sonuç </v-stepper-step>
            </v-stepper-header>

            <div
              class="portfolio-year-end-transition__stepper-body overflow-y-auto"
            >
              <v-stepper-items>
                <v-stepper-content step="1">
                  <portfolio-year-end-transition-setup-step
                    :loading="loading"
                    :form-data="formData"
                    :snapshot="snapshot"
                    :next-period-start="nextPeriodStart"
                  />
                </v-stepper-content>

                <v-stepper-content step="2">
                  <portfolio-year-end-transition-candidates-step
                    :loading="loading || previewState.loading"
                    :rows="resolvedCandidateRows"
                    :totals="candidateMeta"
                    :effective-date="nextPeriodStart"
                    :default-rule-type="formData.default_rule_type"
                    :price-increase-percent="formData.price_increase_percent"
                    :previewable-row-count="previewableRows.length"
                    :excluded-row-count="excludedRowKeys.length"
                    @row-action-select="handleCandidateActionSelect"
                    @exclude-all-ready="excludeAllReadyRows"
                    @restore-excluded="restoreExcludedRows"
                  />
                </v-stepper-content>

                <v-stepper-content step="3">
                  <portfolio-year-end-transition-preview-step
                    :loading="previewState.loading"
                    :applying="applyState.loading"
                    :apply-processed-count="applyState.processed"
                    :apply-total-count="applyState.total"
                    :apply-current-label="applyState.currentLabel"
                    :apply-current-row="applyState.currentRow"
                    :apply-success-count="applySuccessCount"
                    :apply-failed-count="applyFailedCount"
                    :preview-data="previewState.data"
                  />
                </v-stepper-content>

                <v-stepper-content step="4">
                  <portfolio-year-end-transition-results-step
                    :result-data="applyState.data"
                  />
                </v-stepper-content>
              </v-stepper-items>
            </div>
          </v-stepper>
        </v-card-text>

        <v-card-actions
          v-if="showFooterActions"
          class="portfolio-year-end-transition__footer px-6 py-4"
        >
          <v-btn
            v-if="showLeftAuxiliaryFooterAction"
            text
            class="mr-2"
            :disabled="auxiliaryFooterDisabled"
            @click="handleAuxiliaryFooterAction"
          >
            {{ auxiliaryFooterLabel }}
          </v-btn>

          <v-btn
            v-if="showSecondaryFooterAction"
            text
            class="mr-2"
            :disabled="secondaryFooterDisabled"
            @click="handleSecondaryFooterAction"
          >
            {{ secondaryFooterLabel }}
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="showRightAuxiliaryFooterAction"
            text
            class="mr-2"
            :disabled="auxiliaryFooterDisabled"
            @click="handleAuxiliaryFooterAction"
          >
            {{ auxiliaryFooterLabel }}
          </v-btn>

          <v-btn
            v-if="showPrimaryFooterAction"
            color="primary"
            :loading="primaryFooterLoading"
            :disabled="primaryFooterDisabled"
            @click="handlePrimaryFooterAction"
          >
            {{ primaryFooterLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <portfolio-year-end-transition-decision-dialog
      v-model="decisionDialog.open"
      :row="decisionDialog.row"
      :standard-products="catalog.standard_products"
      :existing-override="selectedRowOverride"
      @save="handleDecisionSave"
      @reset="handleDecisionReset"
    />
  </div>
</template>

<script>
import PortfolioYearEndTransitionSetupStep from "./PortfolioYearEndTransitionSetupStep.vue";
import PortfolioYearEndTransitionCandidatesStep from "./PortfolioYearEndTransitionCandidatesStep.vue";
import PortfolioYearEndTransitionPreviewStep from "./PortfolioYearEndTransitionPreviewStep.vue";
import PortfolioYearEndTransitionResultsStep from "./PortfolioYearEndTransitionResultsStep.vue";
import PortfolioYearEndTransitionDecisionDialog from "./PortfolioYearEndTransitionDecisionDialog.vue";

export default {
  name: "PortfolioYearEndTransitionDialog",
  components: {
    PortfolioYearEndTransitionSetupStep,
    PortfolioYearEndTransitionCandidatesStep,
    PortfolioYearEndTransitionPreviewStep,
    PortfolioYearEndTransitionResultsStep,
    PortfolioYearEndTransitionDecisionDialog,
  },
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
    defaultSnapshotDate: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      currentStep: 1,
      loading: false,
      snapshot: {
        yearly_renewal_count: 0,
        monthly_transition_count: 0,
        product_decision_count: 0,
        blocked_count: 0,
      },
      meta: {
        total: 0,
        ready_total: 0,
        needs_decision_total: 0,
        blocked_total: 0,
      },
      catalog: {
        standard_products: [],
      },
      candidateRows: [],
      excludedRowKeys: [],
      rowOverrides: {},
      previewState: {
        loading: false,
        data: null,
      },
      applyState: {
        loading: false,
        data: null,
        processed: 0,
        total: 0,
        currentLabel: null,
        currentRow: null,
      },
      suspendSnapshotWatcher: false,
      decisionDialog: {
        open: false,
        row: null,
      },
      formData: {
        snapshot_date: null,
        include_yearly: true,
        include_monthly: true,
        include_product_decisions: true,
        default_rule_type: "same_product",
        price_increase_percent: null,
      },
    };
  },
  computed: {
    scopedCandidateRows() {
      return (this.candidateRows || []).filter((row) => {
        if (
          !this.formData.include_yearly &&
          row.operation_scope === "yearly_renewal"
        ) {
          return false;
        }

        if (
          !this.formData.include_monthly &&
          row.operation_scope === "monthly_transition"
        ) {
          return false;
        }

        if (
          !this.formData.include_product_decisions &&
          row.wizard_status === "needs_decision"
        ) {
          return false;
        }

        return true;
      });
    },
    resolvedCandidateRows() {
      return this.scopedCandidateRows.map((row) => ({
        ...row,
        excluded: this.excludedRowKeys.includes(row.row_key),
        has_override: !!this.getRowOverride(row.row_key),
        resolved_wizard_status: this.resolveRowStatus(row),
        display_rule_label: this.resolveDisplayRuleLabel(row),
      }));
    },
    candidateMeta() {
      return {
        total: this.resolvedCandidateRows.length,
        ready_total: this.resolvedCandidateRows.filter(
          (row) => row.resolved_wizard_status === "ready"
        ).length,
        needs_decision_total: this.resolvedCandidateRows.filter(
          (row) => row.resolved_wizard_status === "needs_decision"
        ).length,
        blocked_total: this.resolvedCandidateRows.filter(
          (row) => row.resolved_wizard_status === "blocked"
        ).length,
      };
    },
    previewableRows() {
      return this.resolvedCandidateRows.filter(
        (row) => row.resolved_wizard_status === "ready" && !row.excluded
      );
    },
    selectedRowOverride() {
      const rowKey = this.decisionDialog.row?.row_key;

      if (!rowKey) {
        return null;
      }

      return this.getRowOverride(rowKey);
    },
    hasGlobalPriceIncrease() {
      return (
        this.formData.price_increase_percent !== null &&
        this.formData.price_increase_percent !== "" &&
        !Number.isNaN(Number(this.formData.price_increase_percent))
      );
    },
    applySuccessCount() {
      return this.applyState.data?.success_rows?.length || 0;
    },
    applyFailedCount() {
      return this.applyState.data?.failed_rows?.length || 0;
    },
    canNavigateCompletedSteps() {
      if (this.applyState.loading) {
        return false;
      }

      if (this.currentStepNumber < 4) {
        return true;
      }

      return this.applyFailedCount > 0;
    },
    currentStepNumber() {
      return Number(this.currentStep || 1);
    },
    isLoadDisabled() {
      if (
        this.loading ||
        !this.isValidSnapshotDate(this.formData.snapshot_date)
      ) {
        return true;
      }

      if (this.formData.default_rule_type !== "update_price") {
        return false;
      }

      return (
        this.formData.price_increase_percent === null ||
        this.formData.price_increase_percent === "" ||
        Number.isNaN(Number(this.formData.price_increase_percent))
      );
    },
    canApplyPreview() {
      return !!this.previewState.data?.meta?.can_apply;
    },
    showSecondaryFooterAction() {
      return this.currentStepNumber === 2 || this.currentStepNumber === 3;
    },
    showFooterActions() {
      return !(this.currentStepNumber === 3 && this.applyState.loading);
    },
    showLeftAuxiliaryFooterAction() {
      return this.currentStepNumber === 1;
    },
    showRightAuxiliaryFooterAction() {
      return this.currentStepNumber === 3;
    },
    auxiliaryFooterLabel() {
      return this.currentStepNumber === 1 ? "Taslak Yükle" : "Taslağı Kaydet";
    },
    auxiliaryFooterDisabled() {
      if (this.currentStepNumber === 1) {
        return (
          this.loading || this.previewState.loading || this.applyState.loading
        );
      }

      if (this.currentStepNumber === 3) {
        return (
          this.previewState.loading ||
          this.applyState.loading ||
          !this.previewState.data
        );
      }

      return false;
    },
    secondaryFooterLabel() {
      return "Geri";
    },
    secondaryFooterDisabled() {
      return this.applyState.loading;
    },
    showPrimaryFooterAction() {
      return this.currentStepNumber >= 1 && this.currentStepNumber <= 4;
    },
    primaryFooterLabel() {
      if (this.currentStepNumber === 1) {
        return "Adayları Getir";
      }

      if (this.currentStepNumber === 2) {
        return "Önizlemeyi Hazırla";
      }

      if (this.currentStepNumber === 3) {
        return this.applyState.loading ? "Uygulanıyor" : "İşlemleri Uygula";
      }

      return "Bitir";
    },
    primaryFooterLoading() {
      if (this.currentStepNumber === 1) {
        return this.loading;
      }

      if (this.currentStepNumber === 2) {
        return this.previewState.loading;
      }

      if (this.currentStepNumber === 3) {
        return this.applyState.loading;
      }

      return false;
    },
    primaryFooterDisabled() {
      if (this.currentStepNumber === 1) {
        return this.isLoadDisabled;
      }

      if (this.currentStepNumber === 2) {
        return (
          this.loading ||
          this.previewState.loading ||
          this.previewableRows.length === 0
        );
      }

      if (this.currentStepNumber === 3) {
        return (
          this.previewState.loading ||
          this.applyState.loading ||
          !this.canApplyPreview
        );
      }

      return false;
    },
    nextPeriodStart() {
      if (!this.isValidSnapshotDate(this.formData.snapshot_date)) {
        return null;
      }

      const snapshotDate = new Date(this.formData.snapshot_date);

      if (Number.isNaN(snapshotDate.getTime())) {
        return null;
      }

      snapshotDate.setDate(snapshotDate.getDate() + 1);

      const year = snapshotDate.getFullYear();
      const month = String(snapshotDate.getMonth() + 1).padStart(2, "0");
      const day = String(snapshotDate.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.initializeDialog();
      }
    },
    "formData.snapshot_date"(value) {
      if (
        !this.suspendSnapshotWatcher &&
        this.value &&
        this.currentStepNumber === 1 &&
        this.isValidSnapshotDate(value)
      ) {
        this.fetchTransitionData();
      }
    },
  },
  methods: {
    isValidSnapshotDate(value) {
      if (!value || typeof value !== "string") {
        return false;
      }

      const normalizedValue = value.trim();
      const isoPattern = /^20\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

      if (!isoPattern.test(normalizedValue)) {
        return false;
      }

      const [year, month, day] = normalizedValue.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    },
    handleInput(value) {
      if (!value && this.applyState.loading) {
        return;
      }

      this.$emit("input", value);
    },
    closeDialog() {
      if (this.applyState.loading) {
        return;
      }

      this.$emit("input", false);
    },
    resetApplyState() {
      this.applyState.data = null;
      this.applyState.processed = 0;
      this.applyState.total = 0;
      this.applyState.currentLabel = null;
      this.applyState.currentRow = null;
    },
    initializeDialog() {
      this.currentStep = 1;
      this.excludedRowKeys = [];
      this.rowOverrides = {};
      this.previewState.data = null;
      this.resetApplyState();
      this.decisionDialog.open = false;
      this.decisionDialog.row = null;

      if (!this.formData.snapshot_date) {
        this.formData.snapshot_date =
          this.defaultSnapshotDate || this.resolveDefaultSnapshotDate();
      }

      this.fetchTransitionData();
    },
    resolveDefaultSnapshotDate() {
      const today = new Date();
      const currentYear = today.getFullYear();

      return `${currentYear}-12-31`;
    },
    fetchTransitionData({
      advanceToCandidates = false,
      restoredDraft = null,
    } = {}) {
      if (
        this.loading ||
        !this.isValidSnapshotDate(this.formData.snapshot_date)
      ) {
        return;
      }

      this.loading = true;

      this.$api
        .query(
          "api-super-admin/customers/portfolio-summary/year-end-transition",
          {
            params: {
              snapshot_date: this.formData.snapshot_date,
            },
          }
        )
        .then((response) => {
          const payload = response?.data?.data || {};

          this.snapshot = payload.snapshot || this.snapshot;
          this.meta = payload.meta || this.meta;
          this.catalog = payload.catalog || this.catalog;
          this.candidateRows = payload.candidates || [];
          this.previewState.data = null;
          this.resetApplyState();

          if (restoredDraft) {
            this.restoreDraftState(restoredDraft);
          } else {
            this.excludedRowKeys = [];
            this.rowOverrides = {};
          }

          if (advanceToCandidates) {
            this.currentStep = 2;
          }

          if (restoredDraft?.resume_step === "preview") {
            this.$nextTick(() => {
              if (this.previewableRows.length === 0) {
                this.$toast.warning(
                  "Taslak yüklendi, ancak önizlemeye alınacak hazır kayıt kalmadı."
                );
                return;
              }

              this.goToPreview();
            });
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message || "Yenileme verisi yüklenemedi.";

          this.$toast.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleLoadCandidates() {
      this.fetchTransitionData({ advanceToCandidates: true });
    },
    goToPreviousStep() {
      if (this.applyState.loading) {
        return;
      }

      if (this.currentStepNumber === 3) {
        this.currentStep = 2;
        return;
      }

      if (this.currentStepNumber === 2) {
        this.currentStep = 1;
      }
    },
    handleSecondaryFooterAction() {
      this.goToPreviousStep();
    },
    handleAuxiliaryFooterAction() {
      if (this.currentStepNumber === 1) {
        this.openDraftImport();
        return;
      }

      if (this.currentStepNumber === 3) {
        this.exportDraft();
      }
    },
    handlePrimaryFooterAction() {
      if (this.currentStepNumber === 1) {
        this.handleLoadCandidates();
        return;
      }

      if (this.currentStepNumber === 2) {
        this.goToPreview();
        return;
      }

      if (this.currentStepNumber === 3) {
        this.applyTransition();
        return;
      }

      this.handleApplyClose();
    },
    getRowOverride(rowKey) {
      return this.rowOverrides?.[rowKey] || null;
    },
    resolveRowStatus(row) {
      if (row.operation_scope === "yearly_renewal") {
        if (row.wizard_status === "blocked") {
          return "blocked";
        }

        if (
          this.formData.default_rule_type === "update_price" &&
          !this.hasGlobalPriceIncrease
        ) {
          return "needs_decision";
        }

        return "ready";
      }

      if (row.wizard_status === "blocked") {
        return "blocked";
      }

      const override = this.getRowOverride(row.row_key);
      if (
        override &&
        override.product_id &&
        override.amount !== null &&
        override.amount !== ""
      ) {
        return "ready";
      }

      if (
        this.formData.default_rule_type === "same_product" &&
        row.wizard_status === "ready" &&
        row.current_product_id &&
        row.current_product_amount !== null &&
        row.current_product_amount !== undefined
      ) {
        return "ready";
      }

      if (
        this.formData.default_rule_type === "update_price" &&
        this.hasGlobalPriceIncrease &&
        row.wizard_status === "ready" &&
        row.current_product_id &&
        row.current_product_amount !== null &&
        row.current_product_amount !== undefined
      ) {
        return "ready";
      }

      return "needs_decision";
    },
    resolveDisplayRuleLabel(row) {
      if (this.excludedRowKeys.includes(row.row_key)) {
        return "Operasyondan çıkarıldı";
      }

      if (row.operation_scope === "yearly_renewal") {
        if (row.wizard_status === "blocked") {
          return "İşlem üretilemedi";
        }

        if (
          this.formData.default_rule_type === "update_price" &&
          this.hasGlobalPriceIncrease &&
          row.current_product_name
        ) {
          return `${row.current_product_name} ${this.formatPercentLabel(
            this.formData.price_increase_percent
          )} artış ile güncellenecek`;
        }

        return row.suggested_rule_label || row.current_product_label || "-";
      }

      if (row.wizard_status === "blocked") {
        return "İşlem üretilemedi";
      }

      const override = this.getRowOverride(row.row_key);
      if (override) {
        return this.buildOverrideLabel(row, override);
      }

      if (
        this.formData.default_rule_type === "same_product" &&
        row.wizard_status === "ready" &&
        row.current_product_name
      ) {
        return `${row.current_product_name} ile devam`;
      }

      if (this.formData.default_rule_type === "update_price") {
        if (this.hasGlobalPriceIncrease && row.current_product_name) {
          const increasedAmount = this.calculateIncreasedAmount(
            row.current_product_amount,
            this.formData.price_increase_percent
          );

          return `${row.current_product_name} ${this.formatCurrency(
            increasedAmount
          )} ile güncellenecek`;
        }

        return "Fiyat kararı bekleniyor";
      }

      if (this.formData.default_rule_type === "manual_decision") {
        return "Kayıt bazında karar verilecek";
      }

      return row.suggested_rule_label || "Ürün kararı verilecek";
    },
    buildOverrideLabel(row, override) {
      const product = this.findStandardProduct(override.product_id);
      const productName =
        product?.name || row.current_product_name || "Seçili ürün";

      if (override.amount === null || override.amount === "") {
        return `${productName} için fiyat girilecek`;
      }

      const amount = Number(override.amount);
      const currentProductId = Number(row.current_product_id || 0);
      const currentAmount = Number(row.current_product_amount || 0);

      if (
        currentProductId > 0 &&
        currentProductId === Number(override.product_id) &&
        this.areAmountsEqual(currentAmount, amount)
      ) {
        return `${productName} ile devam`;
      }

      if (
        currentProductId > 0 &&
        currentProductId === Number(override.product_id)
      ) {
        return `${productName} ${this.formatCurrency(
          amount
        )} ile güncellenecek`;
      }

      return `${productName} ${this.formatCurrency(amount)} ile geçecek`;
    },
    findStandardProduct(productId) {
      return (this.catalog.standard_products || []).find(
        (item) => Number(item.id) === Number(productId)
      );
    },
    areAmountsEqual(first, second) {
      return Math.abs(Number(first || 0) - Number(second || 0)) < 0.0001;
    },
    calculateIncreasedAmount(amount, percent) {
      return Number(
        (Number(amount || 0) * (1 + Number(percent || 0) / 100)).toFixed(2)
      );
    },
    formatPercentLabel(value) {
      const numericValue = Number(value || 0);
      const formatted = numericValue.toLocaleString("tr-TR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return `%${formatted}`;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    },
    handleCandidateActionSelect(action) {
      if (!action || !action.command) {
        return;
      }

      if (action.command === "toggle-exclude") {
        this.handleToggleExclude(action.item);
        return;
      }

      if (action.command === "edit-row-decision") {
        this.openDecisionDialog(action.item);
        return;
      }

      if (action.command === "reset-row-decision") {
        this.handleDecisionReset(action?.item?.row_key || null);
      }
    },
    handleToggleExclude(item) {
      const rowKey = item?.row_key;

      if (!rowKey) {
        return;
      }

      if (this.excludedRowKeys.includes(rowKey)) {
        this.excludedRowKeys = this.excludedRowKeys.filter(
          (value) => value !== rowKey
        );
        return;
      }

      this.excludedRowKeys = [...this.excludedRowKeys, rowKey];
    },
    openDecisionDialog(row) {
      if (!row || row.operation_scope !== "monthly_transition") {
        return;
      }

      this.decisionDialog.row = row;
      this.decisionDialog.open = true;
    },
    handleDecisionSave(decision) {
      if (!decision?.row_key) {
        return;
      }

      this.$set(this.rowOverrides, decision.row_key, decision);
      this.previewState.data = null;
      this.decisionDialog.open = false;
    },
    handleDecisionReset(rowKey = null) {
      const targetRowKey = rowKey || this.decisionDialog.row?.row_key;

      if (!targetRowKey || !this.getRowOverride(targetRowKey)) {
        return;
      }

      this.$delete(this.rowOverrides, targetRowKey);
      this.previewState.data = null;

      if (!rowKey) {
        this.decisionDialog.open = false;
      }
    },
    excludeAllReadyRows() {
      const readyKeys = this.resolvedCandidateRows
        .filter((row) => row.resolved_wizard_status === "ready")
        .map((row) => row.row_key);

      this.excludedRowKeys = [
        ...new Set([...this.excludedRowKeys, ...readyKeys]),
      ];
      this.previewState.data = null;
    },
    restoreExcludedRows() {
      this.excludedRowKeys = [];
      this.previewState.data = null;
    },
    buildPreviewPayload() {
      return {
        snapshot_date: this.formData.snapshot_date,
        include_yearly: this.formData.include_yearly,
        include_monthly: this.formData.include_monthly,
        include_product_decisions: this.formData.include_product_decisions,
        default_rule_type: this.formData.default_rule_type,
        price_increase_percent: this.formData.price_increase_percent,
        excluded_row_keys: this.excludedRowKeys,
        row_overrides: Object.values(this.rowOverrides),
      };
    },
    buildDraftPayload() {
      return {
        type: "portfolio_renewal_draft",
        version: 1,
        exported_at: new Date().toISOString(),
        resume_step: "preview",
        form_data: {
          snapshot_date: this.formData.snapshot_date,
          include_yearly: this.formData.include_yearly,
          include_monthly: this.formData.include_monthly,
          include_product_decisions: this.formData.include_product_decisions,
          default_rule_type: this.formData.default_rule_type,
          price_increase_percent: this.formData.price_increase_percent,
        },
        excluded_row_keys: [...this.excludedRowKeys],
        row_overrides: Object.values(this.rowOverrides),
      };
    },
    buildDraftFileName() {
      const snapshotDate =
        this.formData.snapshot_date || this.resolveDefaultSnapshotDate();

      return `yenileme-taslagi-${snapshotDate}.json`;
    },
    exportDraft() {
      const payload = this.buildDraftPayload();
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = this.buildDraftFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$toast.success("Taslak JSON olarak indirildi.");
    },
    openDraftImport() {
      const input = this.$refs.draftFileInput;

      if (!input) {
        return;
      }

      input.value = "";
      input.click();
    },
    async handleDraftFileSelected(event) {
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      try {
        const rawText = await file.text();
        const payload = JSON.parse(rawText);
        const normalizedDraft = this.normalizeImportedDraft(payload);

        this.applyImportedDraft(normalizedDraft);
      } catch (error) {
        this.$toast.error(
          error?.message || "Taslak dosyası okunamadı veya doğrulanamadı."
        );
      } finally {
        if (event?.target) {
          event.target.value = "";
        }
      }
    },
    normalizeImportedDraft(payload) {
      if (!payload || typeof payload !== "object") {
        throw new Error("Taslak dosyası geçersiz.");
      }

      if (payload.type !== "portfolio_renewal_draft") {
        throw new Error("Bu dosya yenileme aracı taslağı değil.");
      }

      if (Number(payload.version || 0) !== 1) {
        throw new Error("Taslak dosya versiyonu desteklenmiyor.");
      }

      const importedFormData = payload.form_data || {};
      const snapshotDate = String(importedFormData.snapshot_date || "").trim();

      if (!this.isValidSnapshotDate(snapshotDate)) {
        throw new Error("Taslak içindeki bitiş tarihi geçersiz.");
      }

      const defaultRuleType = [
        "same_product",
        "update_price",
        "manual_decision",
      ].includes(importedFormData.default_rule_type)
        ? importedFormData.default_rule_type
        : "same_product";

      const normalizedPriceIncrease =
        importedFormData.price_increase_percent === null ||
        importedFormData.price_increase_percent === "" ||
        typeof importedFormData.price_increase_percent === "undefined"
          ? null
          : Number(importedFormData.price_increase_percent);

      if (
        normalizedPriceIncrease !== null &&
        Number.isNaN(normalizedPriceIncrease)
      ) {
        throw new Error("Taslak içindeki artış oranı geçersiz.");
      }

      return {
        resume_step:
          payload.resume_step === "preview" ? "preview" : "candidates",
        formData: {
          snapshot_date: snapshotDate,
          include_yearly: importedFormData.include_yearly !== false,
          include_monthly: importedFormData.include_monthly !== false,
          include_product_decisions:
            importedFormData.include_product_decisions !== false,
          default_rule_type: defaultRuleType,
          price_increase_percent: normalizedPriceIncrease,
        },
        excluded_row_keys: Array.isArray(payload.excluded_row_keys)
          ? payload.excluded_row_keys
          : [],
        row_overrides: Array.isArray(payload.row_overrides)
          ? payload.row_overrides
          : [],
      };
    },
    applyImportedDraft(draft) {
      this.previewState.data = null;
      this.resetApplyState();
      this.decisionDialog.open = false;
      this.decisionDialog.row = null;
      this.excludedRowKeys = [];
      this.rowOverrides = {};
      this.currentStep = 1;
      this.suspendSnapshotWatcher = true;
      this.formData = {
        ...this.formData,
        ...draft.formData,
      };

      this.$nextTick(() => {
        this.suspendSnapshotWatcher = false;
        this.fetchTransitionData({
          advanceToCandidates: true,
          restoredDraft: draft,
        });
        this.$toast.success(
          "Taslak yüklendi. Önizleme mevcut veriye göre yeniden hazırlanacak."
        );
      });
    },
    restoreDraftState(draft) {
      const validRowKeys = new Set(
        (this.candidateRows || []).map((row) => row.row_key)
      );

      this.excludedRowKeys = (draft.excluded_row_keys || []).filter((rowKey) =>
        validRowKeys.has(rowKey)
      );

      const restoredOverrides = {};
      (draft.row_overrides || []).forEach((override) => {
        const rowKey = override?.row_key;

        if (!rowKey || !validRowKeys.has(rowKey)) {
          return;
        }

        restoredOverrides[rowKey] = override;
      });

      this.rowOverrides = restoredOverrides;
    },
    goToPreview() {
      if (this.previewableRows.length === 0) {
        this.$toast.warning("Önizlemeye alınacak hazır satır bulunmuyor.");
        return;
      }

      this.previewState.loading = true;
      this.previewState.data = null;

      this.$api
        .post(
          "api-super-admin/customers/portfolio-summary/year-end-transition-preview",
          this.buildPreviewPayload()
        )
        .then((response) => {
          this.previewState.data = response?.data?.data || null;
          this.currentStep = 3;
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "Yenileme önizlemesi oluşturulamadı.";

          this.$toast.error(message);
        })
        .finally(() => {
          this.previewState.loading = false;
        });
    },
    applyTransition() {
      if (this.applyState.loading) {
        return;
      }

      if (!this.previewState.data?.meta?.can_apply) {
        this.$toast.warning("Uygulanacak güvenli satır bulunmuyor.");
        return;
      }

      this.runApplyQueue();
    },
    createApplyResultSeed() {
      const previewData = this.previewState.data || {};
      const skippedRows = [
        ...(previewData.needs_decision_rows || []),
        ...(previewData.blocked_rows || []),
      ];

      return {
        reference_date: previewData.reference_date || null,
        effective_date: this.nextPeriodStart,
        snapshot_date: this.formData.snapshot_date,
        meta: {
          attempted_total: (previewData.preview_rows || []).length,
          success_total: 0,
          failed_total: 0,
          skipped_needs_decision_total: (previewData.needs_decision_rows || [])
            .length,
          skipped_blocked_total: (previewData.blocked_rows || []).length,
          skipped_total: skippedRows.length,
        },
        success_rows: [],
        failed_rows: [],
        skipped_rows: skippedRows,
      };
    },
    buildApplyRowPayload(row) {
      return {
        snapshot_date: this.formData.snapshot_date,
        row: {
          row_key: row.row_key,
          operation_scope: row.operation_scope,
          customer_id: row.customer_id,
          customer_name: row.customer_name,
          billing_type: row.billing_type,
          resolved_product_id: row.resolved_product_id,
          resolved_product_name: row.resolved_product_name,
          resolved_amount: row.resolved_amount,
          resolved_rule_label: row.resolved_rule_label,
          before_summary_label: row.before_summary_label,
          after_summary_label: row.after_summary_label,
          current_period_label: row.current_period_label,
          next_period_label: row.next_period_label,
          effective_date: row.effective_date,
          next_period_starts_on: row.next_period_starts_on,
          next_period_ends_on: row.next_period_ends_on,
          source_action_type: row.source_action_type,
        },
      };
    },
    buildApplyCurrentLabel(row) {
      if (!row) {
        return null;
      }

      const billingLabel = row.billing_type || "Billing";
      return `${row.customer_name || "Müşteri"} * ${billingLabel}`;
    },
    extractApiErrorMessage(error, fallbackMessage) {
      const responseMessage = error?.response?.data?.message;
      if (responseMessage) {
        return responseMessage;
      }

      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        const firstFieldErrors = Object.values(errors).find(
          (messages) => Array.isArray(messages) && messages.length > 0
        );

        if (firstFieldErrors && firstFieldErrors[0]) {
          return firstFieldErrors[0];
        }
      }

      return fallbackMessage;
    },
    async runApplyQueue() {
      const previewRows = this.previewState.data?.preview_rows || [];

      if (previewRows.length === 0) {
        this.$toast.warning("Uygulanacak güvenli satır bulunmuyor.");
        return;
      }

      this.applyState.loading = true;
      this.applyState.data = this.createApplyResultSeed();
      this.applyState.processed = 0;
      this.applyState.total = previewRows.length;
      this.applyState.currentLabel = null;
      this.applyState.currentRow = null;

      try {
        /* eslint-disable no-await-in-loop */
        for (const row of previewRows) {
          this.applyState.currentLabel = this.buildApplyCurrentLabel(row);
          this.applyState.currentRow = row;
          await this.$nextTick();

          try {
            const response = await this.$api.post(
              "api-super-admin/customers/portfolio-summary/year-end-transition-apply-row",
              this.buildApplyRowPayload(row)
            );

            const resultRow = response?.data?.data || {
              ...row,
              result_status: "success",
              result_message: "İşlem uygulandı.",
            };

            this.applyState.data.success_rows.push(resultRow);
          } catch (error) {
            this.applyState.data.failed_rows.push({
              ...row,
              result_status: "failed",
              result_message: this.extractApiErrorMessage(
                error,
                "İşlem uygulanamadı."
              ),
            });
          }

          this.applyState.processed += 1;
          this.applyState.data.meta.success_total =
            this.applyState.data.success_rows.length;
          this.applyState.data.meta.failed_total =
            this.applyState.data.failed_rows.length;
        }
        /* eslint-enable no-await-in-loop */

        this.currentStep = 4;
        this.$emit("applied", this.applyState.data);
      } catch (error) {
        const message = this.extractApiErrorMessage(
          error,
          "Yenileme işlemi uygulanamadı."
        );

        this.$toast.error(message);
      } finally {
        this.applyState.loading = false;
        this.applyState.currentLabel = null;
        this.applyState.currentRow = null;
      }
    },
    handleApplyClose() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.portfolio-year-end-transition {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}

.portfolio-year-end-transition__header {
  border-bottom: 1px solid #ddd;
}

.portfolio-year-end-transition__content {
  min-height: 0;
  overscroll-behavior: contain;
}

.portfolio-year-end-transition__stepper {
  min-height: 0;
}

.portfolio-year-end-transition__stepper-header {
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.portfolio-year-end-transition__stepper-body {
  min-height: 0;
  flex: 1 1 auto;
  overscroll-behavior: contain;
}

.portfolio-year-end-transition__footer {
  position: sticky;
  bottom: 0;
  z-index: 4;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  flex-shrink: 0;
}
</style>
